import { useCallback, useEffect, useState } from "react";

/**
 * Хук, синхронизирующий useState с localStorage. Возвращает то же API,
 * что у `useState`, плюс автоматически перечитывает значение при смене ключа
 * (актуально для UI, где cardId/runId меняется навигацией).
 *
 * Если `storageKey` пустой (`""`) — работает как обычный useState без
 * чтения и записи в localStorage. Используется когда родительский ID
 * (например, runId) ещё не подгружен — чтобы не засорять «дефолтный» ключ.
 *
 * SSR-safe: на сервере (нет `window`) ведёт себя как обычный useState с
 * initial-значением.
 */
export function usePersistentState<T>(
	storageKey: string,
	initial: T,
): [T, (value: T | ((prev: T) => T)) => void] {
	const [state, setState] = useState<T>(() => readStorage(storageKey, initial));

	// Перечитать при смене ключа.
	// biome-ignore lint/correctness/useExhaustiveDependencies: read должен срабатывать только на изменение storageKey, initial фиксирован для жизни компонента
	useEffect(() => {
		setState(readStorage(storageKey, initial));
	}, [storageKey]);

	const setAndPersist = useCallback(
		(next: T | ((prev: T) => T)) => {
			setState((prev) => {
				const value = typeof next === "function" ? (next as (p: T) => T)(prev) : next;
				writeStorage(storageKey, value);
				return value;
			});
		},
		[storageKey],
	);

	return [state, setAndPersist];
}

function readStorage<T>(key: string, fallback: T): T {
	if (typeof window === "undefined" || key === "") return fallback;
	try {
		const raw = window.localStorage.getItem(key);
		if (raw === null) return fallback;
		return JSON.parse(raw) as T;
	} catch {
		return fallback;
	}
}

function writeStorage<T>(key: string, value: T): void {
	if (typeof window === "undefined" || key === "") return;
	try {
		window.localStorage.setItem(key, JSON.stringify(value));
	} catch {
		// quota exceeded / privacy mode — игнор: persist best-effort.
	}
}
